import { Move, PieceType } from "chess.js";

// Frequency is the number of times the achievement is expected to be earned per 100 games.
// This can be tested by script/achievement-balance.ts
export const achievements = [
  {
    id: "king-capture",
    title: "Macbeth's Dagger",
    description: "Capture with the King",
    frequency: 3.60,
    match: (moves: Move[]) => moves.some((m) => m.piece === "k" && m.captured),
    testFENs: [
      ["2k5/8/8/8/8/3p4/3K4/8 w - - 0 1", true],
    ],
  },
  {
    id: "max-pawn-capture",
    title: "David's Sling",
    description: "Find 2 captures by the same Pawn",
    frequency: 0.52,
    match: (moves: Move[]) => capturesFromSamePiece(moves, "p") >= 2,
    testFENs: [
      ["3k4/8/8/8/3p1p2/4P3/8/1K6 w - - 0 1", true],
      ["3k4/8/8/8/3p1p2/2P3P1/8/1K6 w - - 0 1", false],
    ],
  },
  {
    id: "3-pawns-capture",
    title: "Uprising",
    description: "Capture with 3 Pawns",
    frequency: 0.07,
    match: (moves: Move[]) => capturingPiecesCount(moves, "p") >= 3,
    testFENs: [
      ["3k4/8/1n6/2P5/3p1p2/2P3P1/8/1K6 w - - 0 1", true],
      ["3k4/8/1n6/8/1p1p1p2/2P3P1/8/1K6 w - - 0 1", false],
    ],
  },
  {
    id: "4-pawns-capture",
    title: "I'm Spartacus!",
    description: "Capture with 5 Pawns",
    frequency: 0.0,
    match: (moves: Move[]) => capturingPiecesCount(moves, "p") >= 4,
  },
  {
    id: "max-rook-capture",
    title: "The Tower of London",
    description: "Find 4 captures by the same Rook",
    frequency: 0.04,
    match: (moves: Move[]) => capturesFromSamePiece(moves, "r") >= 4,
    testFENs: [
      ["3k4/3b4/8/1p1R1n2/3r4/8/2K5/8 w - - 0 1", true],
      ["3k4/3b4/8/1p1R4/3r4/8/2K5/8 w - - 0 1", false],
      ["3k4/3b2n1/8/3R4/3r4/1p4R1/2K5/8 w - - 0 1", false],
    ],
  },
  {
    id: "max-bishop-capture",
    title: "The Inquisition",
    description: "Find 4 captures by the same Bishop",
    frequency: 0.00,
    match: (moves: Move[]) => capturesFromSamePiece(moves, "b") >= 4,
  },
  {
    id: "4-knight-capture",
    title: "Lancelot's Quest",
    description: "Find 4 captures by the same Knight",
    frequency: 0.16,
    match: (moves: Move[]) => capturesFromSamePiece(moves, "n") >= 4,
  },
  {
    id: "max-knight-capture",
    title: "Knights of the Round Table",
    description: "Find 8 captures by the same Knight",
    frequency: 0.00,
    match: (moves: Move[]) => capturesFromSamePiece(moves, "n") >= 8,
    testFENs: [
      ["1k6/2p1p3/1p3p2/3N4/1n6/2q1r3/8/3K4 w - - 0 1", false],
      ["1k6/2p1p3/1p3p2/3N4/1n3b2/2q1r3/8/3K4 w - - 0 1", true],
    ],
  },
  {
    id: "4-queen-capture",
    title: "Wrath of Cleopatra",
    description: "Find 4 captures by the Queen",
    frequency: 4.34,
    match: (moves: Move[]) =>
      moves.filter((m) => m.piece === "q" && m.captured).length >= 4,
  },
  {
    id: "max-queen-capture",
    title: "I didn't think this was possible",
    description: "Find 8 captures by the Queen",
    frequency: 0,
    match: (moves: Move[]) =>
      moves.filter((m) => m.piece === "q" && m.captured).length >= 8,
  },
  {
    id: "2-king-capture",
    title: "The Great Khan",
    description: "Find 2 captures by the King",
    frequency: 0.07,
    match: (moves: Move[]) => capturesFromSamePiece(moves, "k") >= 2,
  },
  {
    id: "3-king-capture",
    title: "Triumvirate",
    description: "Find 3 captures by the King",
    frequency: 0,
    match: (moves: Move[]) => capturesFromSamePiece(moves, "k") >= 3,
  },
  {
    id: "long-capture",
    title: "Longbow",
    description: "Capture from the other side of the board",
    frequency: 0.26,
    match: (moves: Move[]) =>
      moves.some((m) => m.captured && moveDistance(m) > 8),
  },
  {
    id: "capture-promotion",
    title: "Prometheus Unbound",
    description: "Capture and promote at the same time",
    frequency: 0.06,
    match: (moves: Move[]) => moves.some((m) => m.captured && m.promotion),
  },
  {
    id: "backwards-knight-capture",
    title: "Side Saddle",
    description: "Capture with a backwards Knight move",
    frequency: 4.34,
    match: (moves: Move[]) =>
      moves.some((m) => m.piece === "n" && moveDirection(m) < 0 && m.captured),
  },
  {
    id: "pawn-check",
    title: "Peasant Revolt",
    description: "Check with a Pawn",
    frequency: 5.44,
    match: (moves: Move[]) =>
      moves.some((m) => m.piece === "p" && m.san.at(-1) === "+"),
  },
  {
    id: "rook-double-check",
    title: "Under Siege",
    description: "Find 2 checks from the same Rook",
    frequency: 5.52,
    match: (moves: Move[]) => checksFromSamePiece(moves, "r") >= 2,
    testFENs: [
      ["k7/8/8/8/8/3R4/3K4/8 w - - 0 1", true],
      ["k7/8/8/3q4/1rR5/3R4/3K4/8 w - - 0 1", false],
    ],
  },
  {
    id: "bishop-double-check",
    title: "Test of Faith",
    description: "Find 2 checks from the same Bishop",
    frequency: 1.96,
    match: (moves: Move[]) => checksFromSamePiece(moves, "b") >= 2,
  },
  {
    id: "knight-double-check",
    title: "A Knight's Tale",
    description: "Find 2 checks from the same Knight",
    frequency: 8.36,
    match: (moves: Move[]) => checksFromSamePiece(moves, "n") >= 2,
  },
  {
    id: "queen-3-check",
    title: "Let Them Eat Cake",
    description: "Find 3 checks with the Queen",
    frequency: 13.77,
    match: (moves: Move[]) =>
      moves.filter((m) => m.piece === "q" && isCheckOrMate(m)).length >= 3,
    testFENs: [
      ["kr6/1b6/1r6/1Q6/8/8/8/4K3 w - - 0 1", true],
    ],
  },
  {
    id: "queen-9-check",
    title: "Jezebel's Revenge",
    description: "Find 9 checks with the Queen",
    frequency: 0.14,
    match: (moves: Move[]) =>
      moves.filter((m) => m.piece === "q" && isCheckOrMate(m)).length >= 9,
  },
  {
    id: "castle-check",
    title: "Tower of Babel",
    description: "Deliver a check with a castling move",
    frequency: 0.01,
    match: (moves: Move[]) =>
      moves.some((m) => isCheckOrMate(m) && m.san.includes("O")),
  },
  {
    id: "castle-check",
    title: 'Eric "Retire From Chess" Rosen',
    description: "Deliver a checkmate with a castling move",
    frequency: 0.01,
    match: (moves: Move[]) =>
      moves.some((m) => m.san.at(-1) == "#" && m.san.includes("O")),
  },
  {
    id: "pawn-checkmate",
    title: "The Guillotine",
    description: "Checkmate with a Pawn",
    frequency: 0.24,
    match: (moves: Move[]) =>
      moves.some((m) => m.piece === "p" && m.san.at(-1) === "#"),
  },
  {
    id: "knight-underpromotion-check",
    title: "Charge of the Light Brigade",
    description: "Check with a Knight underpromotion",
    frequency: 0.06,
    match: (moves: Move[]) =>
      moves.some((m) => m.san.at(-1) === "+" && m.promotion === "n"),
  },
  {
    id: "knight-underpromotion-checkmate",
    title: "Cataphract Charge",
    description: "Checkmate with a Knight underpromotion",
    match: (moves: Move[]) =>
      moves.some((m) => m.san.at(-1) === "#" && m.promotion === "n"),
    frequency: 0.00,
    testFENs: [
      ["rn6/pk1P4/qp6/8/8/2R5/8/3K4 w - - 0 1", true],
    ],
  },
  {
    id: "bishop-underpromotion-check",
    title: "Excommunication",
    description: "Check with a Bishop underpromotion",
    frequency: 0.17,
    match: (moves: Move[]) =>
      moves.some((m) => m.san.at(-1) === "+" && m.promotion === "b"),
  },
  {
    id: "bishop-underpromotion-checkmate",
    title: "Divine Intervention",
    description: "Checkmate with a Bishop underpromotion",
    frequency: 0.00,
    match: (moves: Move[]) =>
      moves.some((m) => m.san.at(-1) === "#" && m.promotion === "b"),
    testFENs: [
      ["8/3P4/4R3/k7/8/1Q4p1/4K3/8 w - - 0 1", true],
    ],
  },
  {
    id: "rook-underpromotion-check",
    title: "Jericho",
    description: "Check with a Rook underpromotion",
    frequency: 0.21,
    match: (moves: Move[]) =>
      moves.some((m) => m.san.at(-1) === "+" && m.promotion === "r"),
  },
  {
    id: "rook-underpromotion-checkmate",
    title: "The Eye of Horus",
    description: "Checkmate with a Rook underpromotion",
    frequency: 0.05,
    match: (moves: Move[]) =>
      moves.some((m) => m.san.at(-1) === "#" && m.promotion === "r"),
  },
  {
    id: "en-passant",
    title: "Read The Fine Print",
    description: "Capture en passant",
    frequency: 0.11,
    match: (moves: Move[]) => moves.some((m) => m.flags === "e"),
  },
  {
    id: "en-passant-check",
    title: "Guy Fawkes",
    description: "Check using en passant",
    frequency: 0.02,
    match: (moves: Move[]) =>
      moves.some((m) => m.flags === "e" && m.san.at(-1) === "+"),
  },
  {
    id: "en-passant-checkmate",
    title: "Bolshevik Revolution",
    description: "Checkmate using en passant",
    frequency: 0,
    match: (moves: Move[]) =>
      moves.some((m) => m.flags === "e" && m.san.at(-1) === "#"),
  },
  {
    id: "2-checkmates",
    title: "The King is Dead",
    description: "Find 2 checkmate moves in a position",
    frequency: 0.31,
    match: (moves: Move[]) =>
      moves.filter((m) => m.san.at(-1) === "#").length >= 2,
  },
  {
    id: "3-checkmates",
    title: "Et Tu, Brute?",
    description: "Find 3 checkmate moves in a position",
    frequency: 0.06,
    match: (moves: Move[]) =>
      moves.filter((m) => m.san.at(-1) === "#").length >= 3,
  },
  {
    id: "5-checkmates",
    title: "Overkill",
    description: "Find 5 checkmate moves in a position",
    frequency: 0.04,
    match: (moves: Move[]) =>
      moves.filter((m) => m.san.at(-1) === "#").length >= 5,
  },
  {
    id: `8-captures`,
    title: "Lindisfarne",
    description: "Find 8 captures a position",
    frequency: 0.71,
    match: (moves: Move[]) => moves.filter((m) => m.captured).length >= 8,
  },
  {
    id: `8-checks`,
    title: "Dance of the Eight Immortals",
    description: "Find 8 checks a position",
    frequency: 0.46,
    match: (moves: Move[]) =>
      moves.filter((m) => isCheckOrMate(m)).length >= 8,
  },
];

// Calculate the direction of a move relative to the player's color
const moveDirection = (move: Move) => {
  const fromRank = parseInt(move.from.at(1)!, 10);
  const toRank = parseInt(move.to.at(1)!, 10);
  return move.color === "w" ? toRank - fromRank : fromRank - toRank;
};

// Calculate the euclidian distance between two squares
const moveDistance = (move: Move) => {
  const fromFile = move.from.charCodeAt(0);
  const toFile = move.to.charCodeAt(0);
  const fromRank = parseInt(move.from.at(1)!, 10);
  const toRank = parseInt(move.to.at(1)!, 10);
  return Math.sqrt(
    Math.pow(toFile - fromFile, 2) + Math.pow(toRank - fromRank, 2),
  );
};

// Count the number of distinct pieces of a type that make a capture
const capturingPiecesCount = (moves: Move[], piece: PieceType) => {
  const captures = moves.filter((m) => m.piece === piece && m.captured);
  // Count unique from squares
  const fromSquares = new Set(captures.map((m) => m.from));
  return fromSquares.size;
};

// Count the number of checks from the same piece
const checksFromSamePiece = (moves: Move[], piece: PieceType) => {
  const checks = moves.filter((move) =>
    move.piece === piece && isCheckOrMate(move)
  );
  // Tally the number of checks from each from square
  const fromSquares = tally(checks.map((move) => move.from));
  // Return the maximum number of checks by the same square
  return Math.max(...Object.values(fromSquares));
};

// Count the number of captures by the same piece
const capturesFromSamePiece = (moves: Move[], piece: PieceType) => {
  const captures = moves.filter((move) =>
    move.piece === piece && move.captured
  );
  // Tally the number of captures by each from square
  const fromSquares = tally(captures.map((move) => move.from));
  // Return the maximum number of captures by the same square
  return Math.max(...Object.values(fromSquares));
};

// Tally an array of values into an object
const tally = (values: string[]) =>
  values.reduce((acc, value) => {
    if (acc[value]) {
      acc[value] += 1;
    } else {
      acc[value] = 1;
    }
    return acc;
  }, {} as Record<string, number>);

const isCheckOrMate = (move: Move) =>
  move.san.at(-1) === "+" || move.san.at(-1) === "#";
